'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type { ProductImage } from '@cobrastyle/shared-types';
import { cn } from '@cobrastyle/ui';
import { clampPan, focalPan, hasZoomHeadroom, paintedBox, trackPan, zoomScale } from './image-zoom';

interface ZoomableImageProps {
  image: ProductImage;
  productName: string;
  /** Passed through to the fitted view, which is the PDP's LCP element. */
  priority?: boolean;
}

/**
 * The gallery's main photograph, magnifiable in place.
 *
 * Zoom happens inside the panel rather than in a full-screen overlay: the panel
 * becomes a window onto the file at 1:1, and the reader drags it around. That is
 * the limit too — Torsbo's uploads are 1200px on the long edge (measured across
 * four categories, and the same on the live site), so past 1:1 in CSS pixels
 * there is nothing further to reveal. See image-zoom.ts.
 *
 * TWO images, deliberately. The fitted view stays a next/image variant, because
 * it is the page's LCP element and the optimiser's WebP is a third of the raw
 * JPEG. The original file is only fetched once the reader shows intent — a
 * hover, a focus, or the first tap — so nobody who never zooms pays for it, and
 * by the time they click it is usually already in cache.
 */
/**
 * The widest entry in a srcset, or null if there is nothing to choose from.
 *
 * The zoom layer needs the biggest version of the photograph the app can serve,
 * and it must come through next/image's own proxy rather than straight from the
 * media host: on staging that host is behind HTTP basic auth, so a direct
 * `<img src>` from a browser that has not authenticated to it returns 401 and
 * the panel shows alt text. The proxy is same-origin and the server holds the
 * credentials, so it works in both environments.
 *
 * The optimiser never upscales, so asking for the largest candidate yields the
 * original's own pixels and no more.
 */
export function largestCandidate(srcset: string | null | undefined): string | null {
  if (!srcset) return null;
  let best: { url: string; width: number } | null = null;
  for (const entry of srcset.split(',')) {
    const parts = entry.trim().split(/\s+/);
    if (parts.length < 2) continue;
    const width = Number.parseInt(parts[1], 10);
    if (!Number.isFinite(width)) continue;
    if (!best || width > best.width) best = { url: parts[0], width };
  }
  return best ? best.url : null;
}

export function ZoomableImage({ image, productName, priority }: ZoomableImageProps) {
  const t = useTranslations('product');

  const frameRef = useRef<HTMLButtonElement>(null);
  const fittedRef = useRef<HTMLImageElement>(null);
  /** The proxied URL of the largest version, once the fitted view has one. */
  const [fullSrc, setFullSrc] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  /** The original file's own pixel size, once we have bothered to look. */
  const [full, setFull] = useState<{ width: number; height: number } | null>(null);

  // Before measuring, assume there is something to see. Every image in this
  // catalogue is larger than the panel, so the optimistic answer is right
  // almost always, and it keeps the control from appearing a beat late.
  const scale = full ? zoomScale(full.width, paintedBox(
    frameRef.current?.clientWidth ?? 0,
    frameRef.current?.clientHeight ?? 0,
    full.width,
    full.height,
  ).width) : Infinity;
  const canZoom = !full || hasZoomHeadroom(scale);

  const measure = useCallback(() => {
    if (full) return;
    // Straight through the app's own origin, via the srcset next/image already
    // built — see largestCandidate. Falling back to the raw URL keeps this
    // working wherever the media host is public.
    const src = largestCandidate(fittedRef.current?.srcset) ?? image.url;
    setFullSrc(src);
    const probe = new window.Image();
    probe.src = src;
    const record = () => {
      if (probe.naturalWidth > 0) setFull({ width: probe.naturalWidth, height: probe.naturalHeight });
    };
    if (probe.decode) probe.decode().then(record).catch(record);
    else probe.onload = record;
  }, [full, image.url]);

  // A different photograph is a different set of pixels; keeping the old
  // magnification would show a crop of something nobody asked to look at.
  useEffect(() => {
    setZoomed(false);
    setPan({ x: 0, y: 0 });
    setFull(null);
    setFullSrc(null);
  }, [image.url]);

  useEffect(() => {
    if (!zoomed) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setZoomed(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [zoomed]);

  /** Frame geometry and where the photograph is actually painted inside it. */
  const geometry = () => {
    const frame = frameRef.current;
    if (!frame || !full) return null;
    const width = frame.clientWidth;
    const height = frame.clientHeight;
    return { width, height, painted: paintedBox(width, height, full.width, full.height) };
  };

  const zoomTo = (clientX: number, clientY: number, pointerType: string) => {
    const frame = frameRef.current;
    const geo = geometry();
    if (!frame || !geo || !full) {
      // Not measured yet — zoom anyway; the effect below settles the position
      // as soon as the file's size is known.
      awaitingCentre.current = true;
      setPan({ x: 0, y: 0 });
      setZoomed(true);
      return;
    }
    const box = frame.getBoundingClientRect();
    if (pointerType === 'touch') {
      // A finger gets the point it touched, centred: there is no hover to
      // follow, so where it lands is the whole of the answer.
      const ratio = (client: number, origin: number, start: number, size: number) =>
        size <= 0 ? 0.5 : Math.min(1, Math.max(0, (client - origin - start) / size));
      setPan({
        x: focalPan(ratio(clientX, box.left, geo.painted.left, geo.painted.width), full.width, geo.width),
        y: focalPan(ratio(clientY, box.top, geo.painted.top, geo.painted.height), full.height, geo.height),
      });
    } else {
      // A mouse gets the same mapping its subsequent movement will use, so the
      // view does not jump on the first nudge after clicking.
      setPan({
        x: trackPan((clientX - box.left) / box.width, full.width, geo.width),
        y: trackPan((clientY - box.top) / box.height, full.height, geo.height),
      });
    }
    setZoomed(true);
  };

  // A click can beat the file's own size to the punch. When the size lands
  // afterwards, centre properly rather than leaving the photograph pinned to the
  // panel's corner — but only once per activation, or a drag that happens to end
  // on the corner would yank the view back to the middle.
  const awaitingCentre = useRef(false);
  useEffect(() => {
    if (!zoomed || !full || !awaitingCentre.current) return;
    const frame = frameRef.current;
    if (!frame) return;
    awaitingCentre.current = false;
    setPan({
      x: focalPan(0.5, full.width, frame.clientWidth),
      y: focalPan(0.5, full.height, frame.clientHeight),
    });
  }, [zoomed, full]);

  /** Only ever set for touch: a mouse pans by hovering, with nothing held. */
  const drag = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  /**
   * A finger that panned has also, as far as the DOM is concerned, tapped. This
   * swallows that tap — without it, letting go at the end of a pan zoomed back
   * out, which is the bug that made the whole thing feel unpredictable.
   */
  const swallowNextClick = useRef(false);

  const onPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!zoomed || event.pointerType !== 'touch') return;
    drag.current = { x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const frame = frameRef.current;
    if (!zoomed) return;

    if (event.pointerType === 'touch') {
      const from = drag.current;
      if (!from) return;
      const dx = event.clientX - from.x;
      const dy = event.clientY - from.y;
      // 3px of slop, so a tap with a shaky thumb still counts as a tap. Recorded
      // BEFORE the guard below: a finger that travelled has not tapped, whether
      // or not there was anything for it to pan yet.
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) swallowNextClick.current = true;
      if (!full || !frame) return;
      setPan({
        x: clampPan(from.panX + dx, full.width, frame.clientWidth),
        y: clampPan(from.panY + dy, full.height, frame.clientHeight),
      });
      return;
    }

    if (!full || !frame) return;

    // Mouse and pen: the zoom follows the pointer with no button held, so a
    // click stays unambiguously "zoom out" rather than the end of a drag.
    const box = frame.getBoundingClientRect();
    setPan({
      x: trackPan((event.clientX - box.left) / box.width, full.width, frame.clientWidth),
      y: trackPan((event.clientY - box.top) / box.height, full.height, frame.clientHeight),
    });
  };

  const onPointerUp = () => {
    drag.current = null;
  };

  const onClick = (event: React.MouseEvent) => {
    if (swallowNextClick.current) {
      swallowNextClick.current = false;
      return;
    }
    if (zoomed) {
      setZoomed(false);
      return;
    }
    if (!canZoom) return;
    // A tap arrives with no hover in front of it, so this is the first chance to
    // measure. Without it a touch reader zooms into the fitted variant and the
    // control does nothing they can see.
    measure();
    zoomTo(event.clientX, event.clientY, (event as React.PointerEvent).pointerType ?? 'mouse');
  };

  return (
    <button
      ref={frameRef}
      type="button"
      onClick={onClick}
      onPointerEnter={measure}
      onFocus={measure}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      aria-pressed={zoomed}
      aria-label={zoomed ? t('zoomOut') : t('zoomImage')}
      // `overflow-hidden` is what keeps the magnified file inside the panel
      // instead of spilling over the page — this is the window, not a viewer.
      // `touch-none` while zoomed so a finger pans instead of scrolling the page.
      // The zoomed cursor is `zoom-out`, not `grab`: with a mouse there is
      // nothing to grab, and the only thing a click does is leave.
      className={cn(
        'relative block h-full w-full overflow-hidden',
        zoomed ? 'cursor-zoom-out touch-none' : '',
        !zoomed && canZoom ? 'cursor-zoom-in' : '',
      )}
    >
      {zoomed && full ? (
        // Plain <img> on purpose: this is the untouched original, and an element
        // with no srcset also reports an honest naturalWidth — a next/image
        // element density-corrects it and reads 495 for a file that is 1200.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          data-testid="zoom-layer"
          src={fullSrc ?? image.url}
          alt={image.label || productName}
          draggable={false}
          style={{ width: full.width, height: full.height, left: pan.x, top: pan.y }}
          className="absolute max-w-none mix-blend-multiply"
        />
      ) : zoomed ? (
        // Clicked before the original had been measured: hold the fitted view
        // until it lands rather than flashing an empty panel.
        <img
          data-testid="zoom-layer"
          src={fullSrc ?? image.url}
          alt={image.label || productName}
          draggable={false}
          className="absolute inset-0 h-full w-full object-contain mix-blend-multiply"
        />
      ) : (
        <Image
          ref={fittedRef}
          src={image.url}
          alt={image.label || productName}
          fill
          // `mix-blend-multiply` drops the photo's own white ground onto the
          // tile, so the catalogue's white-background JPEGs stop showing as a
          // bright rectangle inside the panel.
          className="object-contain mix-blend-multiply"
          priority={priority}
          sizes="(max-width: 1024px) 100vw, 55vw"
        />
      )}
    </button>
  );
}
